import type { ReactNode } from 'react';
import { ArrowLeft, Plus } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { useNavigateBack } from '@shared/lib/useNavigateBack';

/** Visitor access itself, where its pages and the scan screen go back to. */
export const VISITOR_ACCESS = '/app/access-invites';

/**
 * Visitor access's header, as the mocks draw it: a flat dark bar with a round back button, the title
 * beside it (centred on Visitor access itself) and an optional action on the right. Back returns to
 * where the member came from, or to `backTo` on a page opened straight from a link.
 */
export function VisitorHeader({
  title,
  backTo,
  action,
  centred,
}: {
  title: string;
  backTo: string;
  action?: ReactNode;
  centred?: boolean;
}): JSX.Element {
  const navigateBack = useNavigateBack();
  const back = () => navigateBack(backTo);

  return (
    <header className="sticky top-0 z-30 bg-primary-800 px-3.5 pb-3 pt-safe-or-3">
      <div className="flex min-h-10 items-center gap-3">
        <button
          type="button"
          aria-label="Back"
          onClick={back}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/25 text-white"
        >
          <ArrowLeft className="h-4.5 w-4.5" strokeWidth={2} />
        </button>
        <h1 className={cn('m-0 min-w-0 flex-1 truncate text-[21px] font-semibold text-white', centred && 'text-center')}>{title}</h1>
        {action}
      </div>
    </header>
  );
}

/** The round "+" on the right of the header. */
export function HeaderAddButton({ label, onClick }: { label: string; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-400 text-white"
    >
      <Plus className="h-4.5 w-4.5" strokeWidth={2.2} />
    </button>
  );
}

/**
 * A Visitor access page: the header (with an optional `headerAction`), the content, and its actions
 * in a white bar pinned to the bottom edge.
 */
export function PageShell({
  title,
  backTo = `${VISITOR_ACCESS}?tab=contacts`,
  headerAction,
  actions,
  children,
}: {
  title: string;
  backTo?: string;
  headerAction?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-h-[100dvh] flex-col bg-paper">
      <VisitorHeader title={title} backTo={backTo} action={headerAction} />
      <div className="flex flex-1 flex-col gap-[18px] px-4 pb-6 pt-[18px]">{children}</div>

      {/* Page actions two to a row; a lone action fills the bar, and a quieter one below spans it (`col-span-2`). */}
      {actions ? (
        <div className="sticky bottom-0 z-20 grid grid-cols-2 gap-2.5 [&>:only-child]:col-span-2 bg-card px-4 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] shadow-[0_-6px_18px_-10px_rgba(28,43,35,0.25)]">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

/** One saved detail: its label, and its value or "None". */
export function DetailRow({ label, value }: { label: string; value?: string | null }): JSX.Element {
  return (
    <div className="flex min-h-[49px] items-center justify-between gap-4 border-b border-hairline text-[15px] last:min-h-12 last:border-b-0">
      <span className="text-ink-secondary">{label}</span>
      <span className="truncate font-semibold text-ink">{value || 'None'}</span>
    </div>
  );
}
